import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { setCurrentPath, clearDrillDown } from '../../store/drilldownSlice';
import { Colors, Spacing, FontSizes, BorderRadius, Shadows } from '../../constants/theme';
import { DrillDownLevel, KpiType } from '../../types/drilldown';
import { 
  PerformanceChart, 
  DepartmentDistribution, 
  SkillsRadar,
  DataChart 
} from '../DataVisualization';
import DataExportService from '../../utils/dataExport';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  width: 90vw;
  max-width: 1200px;
  height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${Shadows.xl};
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalHeader = styled.div`
  padding: ${Spacing.xl}px;
  border-bottom: 1px solid ${Colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: ${FontSizes.xl}px;
  color: ${Colors.textPrimary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.xl}px;
  cursor: pointer;
  padding: ${Spacing.sm}px;
  
  &:hover {
    color: ${Colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${Spacing.xl}px;
`;

const BreadcrumbNav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  margin-bottom: ${Spacing.xl}px;
  flex-wrap: wrap;
`;

const BreadcrumbItem = styled.button<{ active?: boolean }>`
  background: ${props => props.active ? Colors.primary : 'transparent'};
  color: ${props => props.active ? Colors.textPrimary : Colors.textSecondary};
  border: 1px solid ${props => props.active ? Colors.primary : Colors.border};
  padding: ${Spacing.xs}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? Colors.primary : Colors.surfaceHover};
    border-color: ${Colors.primary};
  }
`;

const LevelContent = styled.div`
  display: grid;
  gap: ${Spacing.xl}px;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xl}px;
`;

const DataCard = styled.div<{ clickable?: boolean }>`
  background: ${Colors.background};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.lg}px;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  ${props => props.clickable && `
    &:hover {
      background: ${Colors.surfaceHover};
      border-color: ${Colors.primary};
      transform: translateY(-2px);
      box-shadow: ${Shadows.md};
    }
  `}
`;

const DataLabel = styled.div`
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.sm}px;
  margin-bottom: ${Spacing.xs}px;
`;

const DataValue = styled.div`
  color: ${Colors.textPrimary};
  font-size: ${FontSizes.xl}px;
  font-weight: 600;
`;

const DataSubtext = styled.div`
  color: ${Colors.textSecondary};
  font-size: ${FontSizes.sm}px;
  margin-top: ${Spacing.xs}px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${Spacing.lg}px;
`;

const TableHeader = styled.th`
  background: ${Colors.background};
  color: ${Colors.textSecondary};
  padding: ${Spacing.md}px;
  text-align: left;
  font-size: ${FontSizes.sm}px;
  font-weight: 600;
  border-bottom: 2px solid ${Colors.border};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${Colors.border};
  
  &:hover {
    background: ${Colors.surfaceHover};
  }
`;

const TableCell = styled.td`
  padding: ${Spacing.md}px;
  color: ${Colors.textPrimary};
  font-size: ${FontSizes.sm}px;
`;

const ActionBar = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  margin-bottom: ${Spacing.lg}px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  background: ${Colors.primary};
  color: white;
  border: none;
  padding: ${Spacing.sm}px ${Spacing.lg}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.primaryDark};
  }
`;

interface EnhancedDrillDownModalProps {
  kpiType: KpiType;
  initialValue: number | string;
  onClose: () => void;
}

export const EnhancedDrillDownModal: React.FC<EnhancedDrillDownModalProps> = ({
  kpiType,
  initialValue,
  onClose
}) => {
  const dispatch = useDispatch();
  const { currentPath } = useSelector((state: RootState) => state.drilldown);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [drilldownData, setDrilldownData] = useState<any>(null);

  useEffect(() => {
    loadDrilldownData();
  }, [currentLevel, kpiType]);

  const loadDrilldownData = () => {
    // Generate mock data based on KPI type and level
    const data = generateDrilldownData(kpiType, currentLevel);
    setDrilldownData(data);
  };

  const generateDrilldownData = (kpi: KpiType, level: number) => {
    switch (kpi) {
      case 'totalEmployees':
        return generateEmployeesDrilldown(level);
      case 'attritionRisk':
        return generateAttritionDrilldown(level);
      case 'engagementScore':
        return generateEngagementDrilldown(level);
      case 'leadershipReady':
        return generateLeadershipDrilldown(level);
      case 'timeToFill':
        return generateTimeToFillDrilldown(level);
      case 'diversityScore':
        return generateDiversityDrilldown(level);
      default:
        return null;
    }
  };

  const generateEmployeesDrilldown = (level: number) => {
    const levels = [
      {
        title: 'Department/Function',
        data: [
          { name: 'Engineering', value: 450, trend: '+5%' },
          { name: 'Sales', value: 280, trend: '+8%' },
          { name: 'HR', value: 120, trend: '-2%' },
          { name: 'Marketing', value: 180, trend: '+12%' },
          { name: 'Operations', value: 320, trend: '+3%' },
          { name: 'Finance', value: 150, trend: '+1%' }
        ],
        chart: 'bar'
      },
      {
        title: 'Country/Region',
        data: [
          { name: 'United States', value: 680, departments: 6 },
          { name: 'United Kingdom', value: 320, departments: 5 },
          { name: 'Germany', value: 250, departments: 4 },
          { name: 'France', value: 180, departments: 4 },
          { name: 'Japan', value: 70, departments: 3 }
        ],
        chart: 'pie'
      },
      {
        title: 'Job Level/Grade',
        data: [
          { grade: 'L1-L3 (Junior)', count: 450, percentage: 30 },
          { grade: 'L4-L6 (Mid)', count: 600, percentage: 40 },
          { grade: 'L7-L9 (Senior)', count: 350, percentage: 23 },
          { grade: 'L10+ (Executive)', count: 100, percentage: 7 }
        ],
        chart: 'area'
      },
      {
        title: 'Individual Records',
        data: {
          employees: [
            { id: 'E001', name: 'John Smith', department: 'Engineering', level: 'L5', engagement: 8.2 },
            { id: 'E002', name: 'Sarah Johnson', department: 'Sales', level: 'L7', engagement: 7.8 },
            { id: 'E003', name: 'Michael Chen', department: 'HR', level: 'L6', engagement: 9.1 },
            { id: 'E004', name: 'Emily Davis', department: 'Marketing', level: 'L4', engagement: 8.5 },
            { id: 'E005', name: 'Robert Wilson', department: 'Finance', level: 'L8', engagement: 7.2 }
          ]
        },
        chart: 'table'
      }
    ];
    return levels[level] || levels[0];
  };

  const generateAttritionDrilldown = (level: number) => {
    const levels = [
      {
        title: 'Risk Category',
        data: [
          { name: 'High Risk', value: 15, count: 225, color: Colors.danger },
          { name: 'Medium Risk', value: 25, count: 375, color: Colors.warning },
          { name: 'Low Risk', value: 60, count: 900, color: Colors.success }
        ],
        chart: 'pie'
      },
      {
        title: 'Department/Function',
        data: [
          { name: 'Engineering', risk: 18, highRisk: 35, mediumRisk: 45 },
          { name: 'Sales', risk: 22, highRisk: 55, mediumRisk: 38 },
          { name: 'HR', risk: 8, highRisk: 12, mediumRisk: 18 },
          { name: 'Marketing', risk: 15, highRisk: 28, mediumRisk: 32 },
          { name: 'Operations', risk: 12, highRisk: 25, mediumRisk: 40 },
          { name: 'Finance', risk: 10, highRisk: 15, mediumRisk: 25 }
        ],
        chart: 'bar'
      },
      {
        title: 'Country/Region',
        data: [
          { name: 'United States', risk: 14, factors: ['Compensation', 'Work-Life Balance'] },
          { name: 'United Kingdom', risk: 16, factors: ['Career Growth', 'Management'] },
          { name: 'Germany', risk: 12, factors: ['Work-Life Balance'] },
          { name: 'France', risk: 18, factors: ['Compensation', 'Career Growth'] },
          { name: 'Japan', risk: 10, factors: ['Management'] }
        ],
        chart: 'area'
      },
      {
        title: 'Employee List',
        data: {
          employees: [
            { id: 'E101', name: 'Alice Brown', tenure: '1.2 years', risk: 'High', factors: ['Compensation Gap: -15%', 'No Promotion: 2 years'], score: 3.2 },
            { id: 'E102', name: 'David Lee', tenure: '0.8 years', risk: 'High', factors: ['Manager Change: 3x', 'Workload: +40%'], score: 4.1 },
            { id: 'E103', name: 'Lisa Wang', tenure: '2.5 years', risk: 'Medium', factors: ['Career Growth Limited'], score: 5.8 },
            { id: 'E104', name: 'James Miller', tenure: '4.1 years', risk: 'Low', factors: [], score: 7.9 },
            { id: 'E105', name: 'Maria Garcia', tenure: '1.8 years', risk: 'Medium', factors: ['Work-Life Balance'], score: 6.2 }
          ]
        },
        chart: 'table'
      }
    ];
    return levels[level] || levels[0];
  };

  const generateEngagementDrilldown = (level: number) => {
    const levels = [
      {
        title: 'Department/Function',
        data: [
          { name: 'Engineering', score: 8.2, participation: 92 },
          { name: 'Sales', score: 7.8, participation: 88 },
          { name: 'HR', score: 8.9, participation: 95 },
          { name: 'Marketing', score: 8.1, participation: 90 },
          { name: 'Operations', score: 7.5, participation: 85 },
          { name: 'Finance', score: 7.9, participation: 87 }
        ],
        chart: 'bar'
      },
      {
        title: 'Country/Region',
        data: [
          { name: 'United States', score: 8.1, trend: '+0.3' },
          { name: 'United Kingdom', score: 7.9, trend: '+0.1' },
          { name: 'Germany', score: 8.3, trend: '+0.5' },
          { name: 'France', score: 7.6, trend: '-0.2' },
          { name: 'Japan', score: 8.5, trend: '+0.4' }
        ],
        chart: 'line'
      },
      {
        title: 'Tenure Bands',
        data: [
          { tenure: '0-1 year', score: 7.2, count: 280 },
          { tenure: '1-3 years', score: 7.8, count: 420 },
          { tenure: '3-5 years', score: 8.1, count: 350 },
          { tenure: '5-10 years', score: 8.4, count: 300 },
          { tenure: '10+ years', score: 8.6, count: 150 }
        ],
        chart: 'area'
      },
      {
        title: 'Survey Question Breakdown',
        data: {
          questions: [
            { category: 'Leadership', question: 'I trust my direct manager', score: 8.3 },
            { category: 'Growth', question: 'I see career growth opportunities', score: 7.5 },
            { category: 'Culture', question: 'I feel valued at work', score: 8.1 },
            { category: 'Work-Life', question: 'I have good work-life balance', score: 7.2 },
            { category: 'Compensation', question: 'I am fairly compensated', score: 7.0 }
          ]
        },
        chart: 'radar'
      }
    ];
    return levels[level] || levels[0];
  };

  const generateLeadershipDrilldown = (level: number) => {
    const levels = [
      {
        title: 'Role Criticality',
        data: [
          { criticality: 'High', readiness: 72, roles: 45, successors: 32 },
          { criticality: 'Medium', readiness: 85, roles: 120, successors: 102 },
          { criticality: 'Low', readiness: 92, roles: 80, successors: 74 }
        ],
        chart: 'bar'
      },
      {
        title: 'Department/Function',
        data: [
          { name: 'Engineering', readiness: 78, critical: 15, ready: 12 },
          { name: 'Sales', readiness: 82, critical: 12, ready: 10 },
          { name: 'HR', readiness: 91, critical: 8, ready: 7 },
          { name: 'Marketing', readiness: 75, critical: 10, ready: 8 },
          { name: 'Operations', readiness: 80, critical: 14, ready: 11 },
          { name: 'Finance', readiness: 88, critical: 9, ready: 8 }
        ],
        chart: 'area'
      },
      {
        title: 'Country/Region',
        data: [
          { name: 'United States', readiness: 82, pipeline: 'Strong' },
          { name: 'United Kingdom', readiness: 78, pipeline: 'Moderate' },
          { name: 'Germany', readiness: 85, pipeline: 'Strong' },
          { name: 'France', readiness: 72, pipeline: 'Developing' },
          { name: 'Japan', readiness: 88, pipeline: 'Strong' }
        ],
        chart: 'pie'
      },
      {
        title: 'Role/Incumbent List',
        data: {
          roles: [
            { role: 'VP Engineering', incumbent: 'John Davis', successors: ['Alice Chen (Ready Now)', 'Bob Smith (1-2 years)'], criticality: 'High' },
            { role: 'Director Sales', incumbent: 'Sarah Wilson', successors: ['Mike Johnson (Ready Now)'], criticality: 'High' },
            { role: 'Head of HR', incumbent: 'Emily Brown', successors: ['Lisa Wang (Ready Now)', 'David Lee (2-3 years)'], criticality: 'Medium' },
            { role: 'CFO', incumbent: 'Robert Taylor', successors: ['James Miller (1-2 years)'], criticality: 'High' },
            { role: 'CMO', incumbent: 'Maria Garcia', successors: ['Tom Anderson (Ready Now)'], criticality: 'Medium' }
          ]
        },
        chart: 'table'
      }
    ];
    return levels[level] || levels[0];
  };

  const generateTimeToFillDrilldown = (level: number) => {
    const levels = [
      {
        title: 'Role Type',
        data: [
          { type: 'Technical', days: 48, positions: 125, trend: '-5 days' },
          { type: 'Operational', days: 32, positions: 180, trend: '-2 days' },
          { type: 'Leadership', days: 75, positions: 25, trend: '+3 days' },
          { type: 'Sales', days: 38, positions: 95, trend: '-8 days' },
          { type: 'Support', days: 28, positions: 150, trend: '-4 days' }
        ],
        chart: 'bar'
      },
      {
        title: 'Department/Function',
        data: [
          { name: 'Engineering', days: 52, openings: 45, filled: 38 },
          { name: 'Sales', days: 35, openings: 32, filled: 28 },
          { name: 'HR', days: 28, openings: 12, filled: 11 },
          { name: 'Marketing', days: 42, openings: 22, filled: 18 },
          { name: 'Operations', days: 30, openings: 38, filled: 35 },
          { name: 'Finance', days: 45, openings: 15, filled: 12 }
        ],
        chart: 'line'
      },
      {
        title: 'Country/Region',
        data: [
          { name: 'United States', days: 38, efficiency: 'High' },
          { name: 'United Kingdom', days: 42, efficiency: 'Moderate' },
          { name: 'Germany', days: 45, efficiency: 'Moderate' },
          { name: 'France', days: 50, efficiency: 'Low' },
          { name: 'Japan', days: 35, efficiency: 'High' }
        ],
        chart: 'area'
      },
      {
        title: 'Requisition List',
        data: {
          requisitions: [
            { position: 'Senior Engineer', posted: '2024-01-15', filled: '2024-03-02', days: 46, recruiter: 'Jane Smith' },
            { position: 'Sales Manager', posted: '2024-02-01', filled: '2024-03-08', days: 35, recruiter: 'Tom Wilson' },
            { position: 'HR Specialist', posted: '2024-02-10', filled: '2024-03-05', days: 23, recruiter: 'Lisa Chen' },
            { position: 'Marketing Lead', posted: '2024-01-20', filled: '2024-03-15', days: 54, recruiter: 'Mike Johnson' },
            { position: 'Finance Analyst', posted: '2024-02-05', filled: '2024-03-20', days: 43, recruiter: 'Sarah Brown' }
          ]
        },
        chart: 'table'
      }
    ];
    return levels[level] || levels[0];
  };

  const generateDiversityDrilldown = (level: number) => {
    const levels = [
      {
        title: 'DEI Category',
        data: [
          { category: 'Gender', score: 68, target: 50, status: 'Exceeding' },
          { category: 'Ethnicity', score: 42, target: 40, status: 'On Track' },
          { category: 'Age Group', score: 78, target: 75, status: 'Exceeding' },
          { category: 'Disability', score: 8, target: 10, status: 'Below Target' },
          { category: 'Veterans', score: 12, target: 10, status: 'Exceeding' }
        ],
        chart: 'radar'
      },
      {
        title: 'Department/Function',
        data: [
          { name: 'Engineering', diversity: 65, gender: 35, ethnicity: 40 },
          { name: 'Sales', diversity: 72, gender: 45, ethnicity: 38 },
          { name: 'HR', diversity: 78, gender: 62, ethnicity: 45 },
          { name: 'Marketing', diversity: 70, gender: 55, ethnicity: 42 },
          { name: 'Operations', diversity: 68, gender: 40, ethnicity: 35 },
          { name: 'Finance', diversity: 71, gender: 48, ethnicity: 38 }
        ],
        chart: 'bar'
      },
      {
        title: 'Country/Region',
        data: [
          { name: 'United States', score: 72, compliance: 'Full' },
          { name: 'United Kingdom', score: 68, compliance: 'Full' },
          { name: 'Germany', score: 65, compliance: 'Partial' },
          { name: 'France', score: 70, compliance: 'Full' },
          { name: 'Japan', score: 58, compliance: 'Partial' }
        ],
        chart: 'pie'
      },
      {
        title: 'Diversity Metrics',
        data: {
          breakdown: [
            { group: 'Female Leadership', current: 38, target: 40, trend: '+5%' },
            { group: 'Ethnic Minorities', current: 42, target: 40, trend: '+8%' },
            { group: 'Age 50+', current: 22, target: 20, trend: '+2%' },
            { group: 'People with Disabilities', current: 8, target: 10, trend: '+1%' },
            { group: 'LGBTQ+', current: 6, target: 5, trend: '+2%' }
          ]
        },
        chart: 'table'
      }
    ];
    return levels[level] || levels[0];
  };

  const handleLevelClick = (level: number) => {
    setCurrentLevel(level);
  };

  const handleDrillDown = () => {
    if (currentLevel < 3) {
      setCurrentLevel(currentLevel + 1);
    }
  };

  const handleExport = () => {
    if (drilldownData) {
      DataExportService.exportDrilldownData(drilldownData, kpiType);
    }
  };

  const renderChart = () => {
    if (!drilldownData) return null;

    const { chart, data } = drilldownData;

    switch (chart) {
      case 'bar':
        return (
          <DataChart
            title=""
            data={data}
            type="bar"
            dataKey="value"
            xDataKey="name"
            height={300}
          />
        );
      case 'pie':
        return (
          <DepartmentDistribution data={data} />
        );
      case 'line':
        return (
          <DataChart
            title=""
            data={data}
            type="line"
            dataKey="score"
            xDataKey="name"
            height={300}
          />
        );
      case 'area':
        return (
          <DataChart
            title=""
            data={data}
            type="area"
            dataKey="value"
            xDataKey="name"
            height={300}
          />
        );
      case 'radar':
        if (drilldownData.title === 'Survey Question Breakdown') {
          return (
            <SkillsRadar 
              data={drilldownData.data.questions.map((q: any) => ({
                skill: q.category,
                score: q.score * 10
              }))}
            />
          );
        }
        return (
          <DataChart
            title=""
            data={data}
            type="radar"
            dataKey="score"
            xDataKey="category"
            height={350}
          />
        );
      case 'table':
        return renderTable();
      default:
        return renderCards();
    }
  };

  const renderCards = () => {
    if (!drilldownData || !drilldownData.data) return null;

    return (
      <DataGrid>
        {drilldownData.data.map((item: any, index: number) => (
          <DataCard key={index} clickable={currentLevel < 3} onClick={handleDrillDown}>
            <DataLabel>{item.name || item.type || item.category}</DataLabel>
            <DataValue>{item.value || item.score || item.days || item.readiness}
              {item.score && '/10'}
              {item.days && ' days'}
              {item.readiness && '%'}
            </DataValue>
            {item.trend && <DataSubtext>Trend: {item.trend}</DataSubtext>}
            {item.count && <DataSubtext>Count: {item.count}</DataSubtext>}
            {item.factors && <DataSubtext>Key Factors: {item.factors.join(', ')}</DataSubtext>}
          </DataCard>
        ))}
      </DataGrid>
    );
  };

  const renderTable = () => {
    if (!drilldownData || !drilldownData.data) return null;

    const tableData = drilldownData.data.employees || 
                      drilldownData.data.roles || 
                      drilldownData.data.requisitions || 
                      drilldownData.data.breakdown ||
                      drilldownData.data.questions;

    if (!tableData) return null;

    const columns = Object.keys(tableData[0]);

    return (
      <Table>
        <thead>
          <tr>
            {columns.map(col => (
              <TableHeader key={col}>
                {col.charAt(0).toUpperCase() + col.slice(1).replace(/([A-Z])/g, ' $1')}
              </TableHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.map((row: any, index: number) => (
            <TableRow key={index}>
              {columns.map(col => (
                <TableCell key={col}>
                  {Array.isArray(row[col]) ? row[col].join(', ') : row[col]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
      </Table>
    );
  };

  const getBreadcrumbLabels = () => {
    const labels = ['Overview'];
    switch (kpiType) {
      case 'totalEmployees':
        labels.push('Department/Function', 'Country/Region', 'Job Level', 'Individuals');
        break;
      case 'attritionRisk':
        labels.push('Risk Category', 'Department', 'Country', 'Employee List');
        break;
      case 'engagementScore':
        labels.push('Department', 'Country', 'Tenure', 'Survey Questions');
        break;
      case 'leadershipReady':
        labels.push('Role Criticality', 'Department', 'Country', 'Role List');
        break;
      case 'timeToFill':
        labels.push('Role Type', 'Department', 'Country', 'Requisitions');
        break;
      case 'diversityScore':
        labels.push('DEI Category', 'Department', 'Country', 'Metrics');
        break;
    }
    return labels.slice(0, currentLevel + 2);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{kpiType.replace(/_/g, ' ').toUpperCase()} - Drill Down Analysis</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        <ModalBody>
          <ActionBar>
            <ActionButton onClick={handleExport}>Export Data</ActionButton>
            <ActionButton onClick={() => window.print()}>Print Report</ActionButton>
          </ActionBar>
          
          <BreadcrumbNav>
            {getBreadcrumbLabels().map((label, index) => (
              <React.Fragment key={index}>
                <BreadcrumbItem 
                  active={index === currentLevel}
                  onClick={() => handleLevelClick(index)}
                >
                  {label}
                </BreadcrumbItem>
                {index < currentLevel && <span>→</span>}
              </React.Fragment>
            ))}
          </BreadcrumbNav>

          <LevelContent>
            {drilldownData && (
              <>
                <h3>{drilldownData.title}</h3>
                {renderChart()}
              </>
            )}
          </LevelContent>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EnhancedDrillDownModal;